import { SsuLogoPlaceholder, useFormatAmount, type WalletLedgerRemainingLimit } from '@capsicum/common';
import { useAvailableMarketsQuery, usePartners } from '@capsicum/socios-core';
import { useCallback } from 'react';

import { useLedgerRemainingLimitQuery } from '../data-access/queries/useLedgerRemainingLimitQuery';
import { type ReceiveDetails, type ReceiveFanTokens } from '../models';

/**
 *
 * @param fanTokenCurrencyId
 * Id of the currency of the FT.
 * @returns all the info for receive flow
 */

export const useReceiveDetails = (fanTokenCurrencyId?: string) => {
	const { arePartnersLoading, getPartnerBy } = usePartners();
	const { data: markets, isLoading: isLoadingAvailableMarkets } = useAvailableMarketsQuery();
	const { formatAmount } = useFormatAmount();

	const ledgerRemainingLimitsReducer = useCallback(
		(data: undefined | WalletLedgerRemainingLimit): ReceiveDetails => {
			const receiveFantokens: Array<ReceiveFanTokens> =
				markets?.map(fanToken => {
					const partner = getPartnerBy(p => p.currencyId === fanToken.baseCurrency?.id);

					const logo = partner?.images?.partnerLogo?.url || (SsuLogoPlaceholder as unknown as string);

					return {
						id: fanToken.baseCurrency?.id,
						label: fanToken?.baseCurrency?.label,
						logo,
						maxAmount: formatAmount(fanToken?.maxQuantity),
						shortCode: fanToken?.baseCurrency?.shortCode
					};
				}) ?? [];

			return {
				receiveFantokens,
				remainingAmountBeforeLimit: {
					formattedMaxAmount: formatAmount(data?.remainingAmountBeforeLimit),
					maxAmount: data?.remainingAmountBeforeLimit
				}
			};
		},
		[markets, getPartnerBy, formatAmount]
	);

	const limitQueryResult = useLedgerRemainingLimitQuery(ledgerRemainingLimitsReducer, fanTokenCurrencyId);

	return {
		...limitQueryResult,
		isLoading: limitQueryResult.isLoading || isLoadingAvailableMarkets || arePartnersLoading
	};
};
